import React, { useEffect, useState } from "react";
import { 
  Container, Row, Col, Card, Button, Table, 
  Form, Spinner, Badge, ProgressBar, ButtonGroup
} from "react-bootstrap";
import { 
  FiDownload, FiArrowDown, FiArrowUp, 
  FiClock, FiAlertCircle, FiBox, FiTrendingUp,
  FiDollarSign, FiCalendar, FiHome, FiTool,
  FiPieChart, FiBarChart2, FiActivity, FiDatabase
} from "react-icons/fi";
import Chart from "react-apexcharts";
import { format, subDays, subMonths, subYears } from 'date-fns';
import axiosInstance from "../../services/AxiosInstance";
// API services
import { 
  getAssetGrowthTrend,
  getAssetStatusDistribution,
  getMaintenanceCostByCategory,
  getNearWarrantyExpiry,
  getTopDepartments,
  getAssetRepairTrend
} from "../../services/AnalyticsServices";

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({
    statusDistribution: [],
    topDepartments: [],
    assetGrowth: [],
    maintenanceCost: [],
    warrantyExpiry: [],
    repairTrend: []
  });
  const [filters, setFilters] = useState({
    timeRange: 'month',
    warrantyDays: 30,
    departmentLimit: 5
  });

  // Time range options
  const timeOptions = [
    { value: 'week', label: 'Last Week' },
    { value: 'month', label: 'Last Month' },
    { value: 'year', label: 'Last Year' },
    { value: 'all', label: 'All Time' }
  ];

  // Load all data based on filters
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        const params = {};
        if (filters.timeRange !== 'all') {
          params.time = filters.timeRange;
        }

        const [
          statusRes,
          departmentsRes,
          growthRes,
          maintenanceRes,
          warrantyRes,
          repairRes
        ] = await Promise.all([
          getAssetStatusDistribution(params),
          getTopDepartments({ ...params, limit: filters.departmentLimit }),
          getAssetGrowthTrend(params),
          getMaintenanceCostByCategory(params),
          getNearWarrantyExpiry({ days: filters.warrantyDays }),
          getAssetRepairTrend(params)
        ]);

        setData({
          statusDistribution: statusRes.data.data || [],
          topDepartments: departmentsRes.data.data || [],
          assetGrowth: growthRes.data.data || [],
          maintenanceCost: maintenanceRes.data.data || [],
          warrantyExpiry: warrantyRes.data.data || [],
          repairTrend: repairRes.data.data || []
        });
      } catch (error) {
        console.error("Error loading analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  // Status distribution chart
  const statusChart = {
    options: {
      chart: {
        type: 'donut',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      labels: data.statusDistribution.map(item => {
        const statusMap = {
          'IN_STOCK': 'In Stock',
          'ALLOCATED': 'In Use',
          'IN_REPAIR': 'In Repair',
          'DISPOSED': 'Disposed'
        };
        return statusMap[item.status] || item.status;
      }),
      colors: ["#28a745", "#007bff", "#fd7e14", "#dc3545"],
      legend: {
        position: "bottom",
        horizontalAlign: 'center',
        fontSize: '13px',
        itemMargin: {
          horizontal: 10,
          vertical: 5
        }
      },
      dataLabels: { 
        enabled: true,
        formatter: function (val) {
          return Math.round(val) + "%";
        },
        dropShadow: {
          enabled: false
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Assets',
                color: '#6c757d',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                }
              }
            }
          }
        }
      },
      tooltip: {
        y: {
          formatter: function(value, { seriesIndex }) {
            const total = data.statusDistribution.reduce((sum, item) => sum + item.count, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${value} (${percentage}%)`;
          }
        }
      }
    },
    series: data.statusDistribution.map(item => item.count)
  };

  // Asset growth chart (combined with repair trend)
  const growthChart = {
    options: {
      chart: {
        type: 'line',
        height: 350,
          toolbar: { show: true },
        zoom: { enabled: true }
      },
      stroke: { 
        curve: 'smooth', 
        width: 3,
        dashArray: [0, 4] // solid for assets, dashed for repairs
      },
      xaxis: {
        categories: data.assetGrowth.map(item => item.month),
        labels: { 
          style: { 
            colors: '#6c757d',
            fontSize: '12px'
          } 
        },
        type: 'datetime',
        tooltip: {
          enabled: false
        }
      },
      yaxis: [
        {
          title: {
            text: "Assets Added",
            style: {
              color: '#6610f2'
            }
          },
          labels: { 
            style: { 
              colors: '#6610f2' 
            },
            formatter: (value) => Math.round(value)
          }
        },
        {
          opposite: true,
          title: {
            text: "Repairs",
            style: {
              color: '#fd7e14'
            }
          },
          labels: { 
            style: { 
              colors: '#fd7e14' 
            },
            formatter: (value) => Math.round(value)
          }
        }
      ],
      colors: ["#6610f2", "#fd7e14"],
      grid: { 
        borderColor: '#e9ecef',
        strokeDashArray: 4
      },
      markers: { 
        size: 5,
        hover: {
          size: 7
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: 'MMM yyyy'
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '13px'
      }
    },
    series: [
      {
        data: data.assetGrowth.map(item => item.total)
      },
      {
        data: data.repairTrend.map(item => item.repairs)
      }
    ]
  };

  // Maintenance cost chart
  const maintenanceChart = {
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: true },
        stacked: false
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          barHeight: '70%',
          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: 0,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        formatter: (val) => `$${val.toLocaleString()}`,
        dropShadow: {
          enabled: false
        }
      },
      xaxis: {
        categories: data.maintenanceCost.map(item => item.category),
        labels: { 
          style: { 
            colors: '#6c757d',
            fontSize: '12px'
          },
          formatter: (value) => `$${Number(value).toLocaleString()}`
        },
        title: {
          text: 'Cost ($)',
          style: {
            color: '#6c757d',
            fontSize: '13px'
          }
        }
      },
      yaxis: {
        labels: {
          style: { 
            colors: '#6c757d',
            fontSize: '12px'
          }
        }
      },
      colors: ["#20c997"],
      grid: { 
        borderColor: '#e9ecef',
        strokeDashArray: 4
      },
      tooltip: {
        y: {
          formatter: (val) => `$${val.toLocaleString()}`
        }
      }
    },
    series: [{
      name: "Maintenance Cost",
      data: data.maintenanceCost.map(item => item.total_maintenance)
    }]
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusInfo = {
      'IN_STOCK': { variant: 'success', text: 'In Stock', icon: <FiBox className="me-1" /> },
      'ALLOCATED': { variant: 'primary', text: 'In Use', icon: <FiHome className="me-1" /> },
      'IN_REPAIR': { variant: 'warning', text: 'In Repair', icon: <FiTool className="me-1" /> },
      'DISPOSED': { variant: 'danger', text: 'Disposed', icon: <FiAlertCircle className="me-1" /> }
    };
    
    const current = statusInfo[status] || { variant: 'secondary', text: status, icon: <FiDatabase className="me-1" /> };
    return (
      <Badge bg={current.variant} className="d-flex align-items-center">
        {current.icon}
        {current.text}
      </Badge>
    );
  };

  // Handle export function
  const handleExport = async (endpoint, filename) => {
    try {
      const response = await axiosInstance.get(endpoint, {
        params: { ...filters, export: true },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Calculate summary metrics
  const totalAssets = data.statusDistribution.reduce((sum, item) => sum + item.count, 0);
  const assetsInUse = data.statusDistribution.find(item => item.status === 'ALLOCATED')?.count || 0;
  const maintenanceTotal = data.maintenanceCost.reduce((sum, item) => sum + item.total_maintenance, 0);
  const latestGrowth = data.assetGrowth[data.assetGrowth.length - 1]?.total || 0;
  const previousGrowth = data.assetGrowth.length > 1 ? data.assetGrowth[data.assetGrowth.length - 2]?.total || 0 : 0;
  const growthPercentage = previousGrowth > 0 ? ((latestGrowth - previousGrowth) / previousGrowth * 100).toFixed(1) : 0;

  return (
    <Container fluid className="py-0 mt-0">
      <Row className="mb-2">
        <Col>
          <h2 className="fw-bold mb-3">
            <FiActivity className="me-2" />
            Asset Analytics Dashboard
          </h2>
          <div className="d-flex align-items-center">
            <ButtonGroup className="me-3">
              <Button 
                variant={activeTab === 'overview' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('overview')}
              >
                <FiPieChart className="me-1" /> Overview
              </Button>
              <Button 
                variant={activeTab === 'trends' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('trends')}
              >
                <FiTrendingUp className="me-1" /> Trends
              </Button>
              <Button 
                variant={activeTab === 'details' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('details')}
              >
                <FiDatabase className="me-1" /> Detailed Data
              </Button>
            </ButtonGroup>
            
            <Form.Select 
              size="sm" 
              value={filters.timeRange}
              onChange={(e) => setFilters({...filters, timeRange: e.target.value})}
              className="me-2"
              style={{ width: '150px' }}
            >
              {timeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
            
            {activeTab === 'details' && (
              <Form.Select 
                size="sm" 
                value={filters.warrantyDays}
                onChange={(e) => setFilters({...filters, warrantyDays: e.target.value})}
                style={{ width: '180px' }}
              >
                <option value="30">Next 30 Days</option>
                <option value="60">Next 60 Days</option>
                <option value="90">Next 90 Days</option>
                <option value="180">Next 180 Days</option>
              </Form.Select>
            )}
          </div>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading analytics data...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards - Always visible */}
          <Row className="mb-2 g-2">
            <Col md={6} lg={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                      <FiBox size={24} className="text-primary" />
                    </div>
                    <div>
                      <h6 className="mb-1 ">Total Assets</h6>
                      <h3 className="mb-0"> 
                        {totalAssets.toLocaleString()}
                      </h3>
                      <small className="">Across all statuses</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                      <FiHome size={24} className="text-success" />
                    </div>
                    <div>
                      <h6 className="mb-1 ">Assets In Use</h6>
                      <h3 className="mb-0">
                        {assetsInUse.toLocaleString()}
                        <small className={`ms-2 ${(assetsInUse / totalAssets * 100) > 70 ? 'text-success' : 'text-warning'}`}>
                          ({Math.round(assetsInUse / totalAssets * 100)}%)
                        </small>
                      </h3>
                      <small className="">Allocated to departments</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                      <FiDollarSign size={24} className="text-warning" />
                    </div>
                    <div>
                      <h6 className="mb-1 ">Maintenance Cost</h6>
                      <h3 className="mb-0">
                        ${maintenanceTotal.toLocaleString()}
                      </h3>
                      <small className="">Current period</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                      <FiAlertCircle size={24} className="text-danger" />
                    </div>
                    <div>
                      <h6 className="mb-1 ">Warranty Expiring</h6>
                      <h3 className="mb-0">
                        {data.warrantyExpiry.length}
                        <small className=" ms-1">items</small>
                      </h3>
                      <small className="">Next {filters.warrantyDays} days</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <Row className="mb-4 g-4">
              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <FiPieChart className="me-2" />
                          Asset Status Distribution
                        </h5>
                        <p className="small mb-0 ">Current allocation of all assets</p>
                      </div>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleExport('/api/Analytics/asset-status-distribution/', 'asset_status.csv')}
                      >
                        <FiDownload className="me-1" /> Export
                      </Button>
                    </div>
                    <Chart 
                      options={statusChart.options} 
                      series={statusChart.series} 
                      type="donut" 
                      height={350} 
                    />
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <FiBarChart2 className="me-2" />
                          Top Departments
                        </h5>
                        <p className="small mb-0 ">By number of allocated assets</p>
                      </div>
                      <div>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleExport('/api/Analytics/top-departments/', 'top_departments.csv')}
                        >
                          <FiDownload className="me-1" /> Export
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Department</th>
                            <th className="text-end">Asset Count</th>
                            <th className="text-end">% of Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.topDepartments.map((dept, index) => {
                            const percentage = Math.round((dept.asset_count / totalAssets) * 100);
                            
                            return (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FiHome className="me-2 " />
                                    {dept.name}
                                  </div>
                                </td>
                                <td className="text-end">{dept.asset_count.toLocaleString()}</td>
                                <td className="text-end">
                                  <div className="d-flex align-items-center justify-content-end">
                                    <span className="me-2">{percentage}%</span>
                                    <ProgressBar 
                                      now={percentage} 
                                      style={{ width: '100px', height: '6px' }} 
                                      variant={index % 2 === 0 ? 'primary' : 'info'}
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Trends Tab */}
          {activeTab === 'trends' && (
            <Row className="mb-4 g-4">
              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <FiTrendingUp className="me-2" />
                          Asset Acquisition & Repairs
                        </h5>
                        <p className="small mb-0 ">Monthly trends over time</p>
                      </div>
                      <div>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleExport('/api/Analytics/asset-growth-trend/', 'asset_growth.csv')}
                          className="me-2"
                        >
                          <FiDownload className="me-1" /> Growth
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          size="sm"
                          onClick={() => handleExport('/api/Analytics/asset-repair/', 'repairs.csv')}
                        >
                          <FiDownload className="me-1" /> Repairs
                        </Button>
                      </div>
                    </div>
                    <Chart 
                      options={growthChart.options} 
                      series={growthChart.series} 
                      type="line" 
                      height={350} 
                    />
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <FiDollarSign className="me-2" />
                          Maintenance Costs by Category
                        </h5>
                        <p className="small mb-0 ">Highest cost categories</p>
                      </div>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => handleExport('/api/Analytics/maintenance-cost-by-category/', 'maintenance_costs.csv')}
                      >
                        <FiDownload className="me-1" /> Export
                      </Button>
                    </div>
                    <Chart 
                      options={maintenanceChart.options} 
                      series={maintenanceChart.series} 
                      type="bar" 
                      height={350} 
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Detailed Data Tab */}
          {activeTab === 'details' && (
            <Row className="g-4">
              <Col lg={12}>
                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          <FiAlertCircle className="me-2" />
                          Assets Nearing Warranty Expiry
                        </h5>
                        <p className="small mb-0 ">
                          {data.warrantyExpiry.length} assets expiring in next {filters.warrantyDays} days
                        </p>
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleExport('/api/Analytics/near-warranty-expiry/', 'warranty_expiry.csv')}
                      >
                        <FiDownload className="me-1" /> Export
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Asset Name</th>
                            <th>Asset Number</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th className="text-end">Warranty End</th>
                            <th className="text-end">Days Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.warrantyExpiry.length > 0 ? (
                            data.warrantyExpiry.map((asset, index) => {
                              const endDate = new Date(asset.warranty_end_date);
                              const today = new Date();
                              const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                              
                              return (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <FiBox className="me-2 " />
                                      {asset.name}
                                    </div>
                                  </td>
                                  <td>{asset.asset_number}</td>
                                  <td>{asset.sub_category__category__name || 'N/A'}</td>
                                  <td>
                                    <StatusBadge status={asset.status} />
                                  </td>
                                  <td className="text-end">
                                    {format(endDate, 'MMM dd, yyyy')}
                                  </td>
                                  <td className="text-end">
                                    <Badge bg={daysRemaining < 15 ? 'danger' : daysRemaining < 30 ? 'warning' : 'info'}>
                                      {daysRemaining} days
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4 ">
                                No assets with expiring warranty in selected period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default Analytics;